import { apiClient } from './';
import { AxiosResponse } from 'axios';

// 네이버 커머스 모니터링 API 타입 정의
export interface ApiStatus {
  timestamp: string;
  queue: QueueStatus;
  rateLimit: RateLimitStatus;
  quota: QuotaStatus;
  health: HealthStatus;
}

export interface QueueStatus {
  queueSize: number;
  processingCount: number;
  completedCount: number;
  failedCount: number;
  lastProcessedAt?: string;
}

export interface RateLimitStatus {
  tokens: number;
  burstTokens: number;
  resetTime: string;
  windowSize: number;
  requestsPerWindow: number;
}

export interface QuotaStatus {
  dailyQuota: number;
  usedQuota: number;
  remainingQuota: number;
  resetTime: string;
}

export interface HealthStatus {
  overall: 'healthy' | 'warning' | 'critical';
  issues: string[];
  timestamp: string;
  redis: boolean;
}

export interface Stats {
  date: string;
  stats: any;
  rateLimit: RateLimitStatus;
  summary: {
    totalRequests: number;
    successRate: number;
    retryRate: number;
    completed: number;
    failed: number;
    retried: number;
  };
}

export interface DashboardData {
  timestamp: string;
  overview: {
    queueSize: number;
    processingCount: number;
    availableTokens: number;
    burstTokens: number;
    health: HealthStatus;
  };
  rateLimit: RateLimitStatus;
  quota: QuotaStatus;
  stats: {
    today: any;
    summary: {
      totalRequests: number;
      successRate: number;
      retryRate: number;
      completed: number;
      failed: number;
      retried: number;
    };
  };
  recentErrors: Array<{
    timestamp: string;
    method: string;
    params?: any;
    error: {
      message: string;
      stack?: string;
      code?: string;
      status?: number;
    };
  }>;
  alerts: Array<{
    type: 'warning' | 'critical';
    message: string;
    timestamp: string;
  }>;
}

export interface RedisStatus {
  status: 'healthy' | 'error';
  available: boolean;
  timestamp: string;
  message: string;
}

export interface HealthCheckResult {
  status: 'healthy' | 'warning' | 'critical';
  timestamp: string;
  details: {
    queue: {
      status: 'healthy' | 'warning';
      size: number;
    };
    rateLimit: {
      status: 'healthy' | 'warning';
      tokens: number;
    };
    processing: {
      status: 'healthy' | 'warning';
      count: number;
    };
    redis: {
      status: 'healthy' | 'error';
      available: boolean;
    };
  };
}

// API 호출 함수들
export const getNaverCommerceApiStatus = async (): Promise<AxiosResponse<ApiStatus>> => {
  return apiClient.get<ApiStatus>('/admin/v1/naver-commerce/monitoring/status');
};

export const getNaverCommerceQueueStatus = async (): Promise<AxiosResponse<QueueStatus>> => {
  return apiClient.get<QueueStatus>('/admin/v1/naver-commerce/monitoring/queue');
};

export const getNaverCommerceRateLimitStatus = async (): Promise<AxiosResponse<RateLimitStatus>> => {
  return apiClient.get<RateLimitStatus>('/admin/v1/naver-commerce/monitoring/rate-limit');
};

export const getNaverCommerceStats = async (date?: string): Promise<AxiosResponse<Stats>> => {
  const params = date ? { date } : {};
  return apiClient.get<Stats>('/admin/v1/naver-commerce/monitoring/stats', { params });
};

export const getNaverCommerceDashboard = async (): Promise<AxiosResponse<DashboardData>> => {
  return apiClient.get<DashboardData>('/admin/v1/naver-commerce/monitoring/dashboard');
};

export const resetNaverCommerceRateLimit = async (): Promise<AxiosResponse<{ message: string; timestamp: string }>> => {
  return apiClient.post<{ message: string; timestamp: string }>('/admin/v1/naver-commerce/monitoring/reset-rate-limit');
};

export const getNaverCommerceRedisStatus = async (): Promise<AxiosResponse<RedisStatus>> => {
  return apiClient.get<RedisStatus>('/admin/v1/naver-commerce/monitoring/redis-status');
};

export const forceCleanupNaverCommerce = async (): Promise<
  AxiosResponse<{
    message: string;
    cleanedQueueItems: number;
    rateLimitReset: boolean;
    timestamp: string;
  }>
> => {
  return apiClient.post<{
    message: string;
    cleanedQueueItems: number;
    rateLimitReset: boolean;
    timestamp: string;
  }>('/admin/v1/naver-commerce/monitoring/force-cleanup');
};

export const getNaverCommerceHealthCheck = async (): Promise<AxiosResponse<HealthCheckResult>> => {
  return apiClient.get<HealthCheckResult>('/admin/v1/naver-commerce/monitoring/health-check');
};

// API 호출 내역 관련 타입 정의
export interface ApiCall {
  id: string;
  url: string;
  method: string;
  statusCode: number;
  requestTime: string;
  timestamp: string;
  errorMessage?: string;
  responseTime?: number;
}

export interface ApiCallsResponse {
  calls: ApiCall[];
  total: number;
  limit: number;
  offset: number;
  timestamp: string;
}

export interface ApiCallsByStatusCodeResponse {
  calls: ApiCall[];
  statusCode: number;
  total: number;
  limit: number;
  offset: number;
  timestamp: string;
}

export interface ApiCallsByUrlResponse {
  calls: ApiCall[];
  url: string;
  total: number;
  limit: number;
  offset: number;
  timestamp: string;
}

export interface ApiCallsStats {
  totalCalls: number;
  successCalls: number;
  errorCalls: number;
  successRate: number;
  errorRate: number;
  statusCodeStats: Record<number, number>;
  urlStats: Record<string, number>;
  timestamp: string;
}

export interface CleanupResponse {
  message: string;
  cleanedCount: number;
  timestamp: string;
}

// API 호출 내역 조회 함수들
export const getNaverCommerceApiCalls = async (
  limit: number = 100,
  offset: number = 0,
  statusCode?: number,
  url?: string,
  showFailedOnly?: boolean,
): Promise<AxiosResponse<ApiCallsResponse>> => {
  const params: any = { limit, offset };

  if (statusCode) {
    params.statusCode = statusCode;
  }
  if (url) {
    params.url = url;
  }
  if (showFailedOnly) {
    params.isError = true;
  }

  return apiClient.get<ApiCallsResponse>('/admin/v1/naver-commerce/monitoring/api-calls', {
    params,
  });
};

export const getNaverCommerceApiCallsByStatusCode = async (
  statusCode: number,
  limit: number = 100,
  offset: number = 0,
): Promise<AxiosResponse<ApiCallsByStatusCodeResponse>> => {
  return apiClient.get<ApiCallsByStatusCodeResponse>(
    `/admin/v1/naver-commerce/monitoring/api-calls/status-code/${statusCode}`,
    {
      params: { limit, offset },
    },
  );
};

export const getNaverCommerceApiCallsByUrl = async (
  url: string,
  limit: number = 100,
  offset: number = 0,
): Promise<AxiosResponse<ApiCallsByUrlResponse>> => {
  return apiClient.get<ApiCallsByUrlResponse>('/admin/v1/naver-commerce/monitoring/api-calls/url', {
    params: { url, limit, offset },
  });
};

export const getNaverCommerceApiCallsStats = async (): Promise<AxiosResponse<ApiCallsStats>> => {
  return apiClient.get<ApiCallsStats>('/admin/v1/naver-commerce/monitoring/api-calls/stats');
};

export const cleanupNaverCommerceApiCalls = async (): Promise<AxiosResponse<CleanupResponse>> => {
  return apiClient.post<CleanupResponse>('/admin/v1/naver-commerce/monitoring/api-calls/cleanup');
};

// 큐 모니터링 관련 타입 정의
export interface QueueItem {
  id: string;
  method: string;
  params: any;
  priority: 'high' | 'normal' | 'low';
  status: 'pending' | 'processing' | 'completed' | 'failed';
  createdAt: string;
  scheduledAt?: string;
  retryCount: number;
  maxRetries: number;
  errorMessage?: string;
  completedAt?: string;
}

export interface PendingApiResponse {
  items: QueueItem[];
  total: number;
  limit?: number;
  timestamp: string;
}

export interface ProcessingApiResponse {
  items: QueueItem[];
  total: number;
  timestamp: string;
}

export interface QueueItemsDetailedResponse {
  pending: {
    high: QueueItem[];
    normal: QueueItem[];
    low: QueueItem[];
    scheduled: QueueItem[];
  };
  processing: QueueItem[];
  summary: {
    totalPending: number;
    totalProcessing: number;
    pendingByPriority: {
      high: number;
      normal: number;
      low: number;
    };
    pendingByMethod: Record<string, number>;
    scheduledCount: number;
  };
  timestamp: string;
}

// 큐 모니터링 API 함수들
export const getPendingApiItems = async (limit?: number): Promise<AxiosResponse<PendingApiResponse>> => {
  return apiClient.get<PendingApiResponse>('/admin/v1/naver-commerce/monitoring/pending-apis', {
    params: limit ? { limit } : {},
  });
};

export const getProcessingApiItems = async (): Promise<AxiosResponse<ProcessingApiResponse>> => {
  return apiClient.get<ProcessingApiResponse>('/admin/v1/naver-commerce/monitoring/processing-apis');
};

export const getQueueItemsDetailed = async (): Promise<AxiosResponse<QueueItemsDetailedResponse>> => {
  return apiClient.get<QueueItemsDetailedResponse>('/admin/v1/naver-commerce/monitoring/queue-items-detailed');
};

export const getPendingApiByPriority = async (
  priority: 'high' | 'normal' | 'low',
  limit?: number,
): Promise<AxiosResponse<PendingApiResponse>> => {
  return apiClient.get<PendingApiResponse>(`/admin/v1/naver-commerce/monitoring/pending-apis/priority/${priority}`, {
    params: limit ? { limit } : {},
  });
};

export const getScheduledApiItems = async (limit?: number): Promise<AxiosResponse<PendingApiResponse>> => {
  return apiClient.get<PendingApiResponse>('/admin/v1/naver-commerce/monitoring/pending-apis/scheduled', {
    params: limit ? { limit } : {},
  });
};

export const getPendingApiByMethod = async (
  method: string,
  limit?: number,
): Promise<AxiosResponse<PendingApiResponse>> => {
  return apiClient.get<PendingApiResponse>(`/admin/v1/naver-commerce/monitoring/pending-apis/method/${method}`, {
    params: limit ? { limit } : {},
  });
};
